import React from 'react';
import { Inbox } from 'lucide-react';

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>;

interface EmptyStateProps {
  icon?: React.ReactNode | IconComponent;
  title?: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  iconSize?: number;
  className?: string;
}

/**
 * EmptyState - Estado vazio padronizado
 *
 * Props:
 * - icon: Componente Lucide (default: Inbox)
 * - title: Título (default: "Nenhum registro encontrado")
 * - description: Texto explicativo
 * - action: Botão ou elemento de ação
 * - iconSize: Tamanho do ícone (default: 48)
 * - className: Classes CSS adicionais
 */
const EmptyState = ({
  icon,
  title = 'Nenhum registro encontrado',
  description,
  action,
  iconSize = 48,
  className = ''
}: EmptyStateProps) => {
  const renderIcon = () => {
    if (!icon) return <Inbox size={iconSize} />;
    if (React.isValidElement(icon)) return icon;
    // boundary: post-isValidElement guard narrows icon to non-JSX branch (IconComponent) but TS
    // can't eliminate ReactNode via the discriminator; two-step cast per plan convention.
    const Icon = icon as unknown as IconComponent;
    return <Icon size={iconSize} />;
  };

  return (
    <div className={`empty-state ${className}`.trim()}>
      <div className="empty-state__icon">
        {renderIcon()}
      </div>
      <h3 className="empty-state__title">{title}</h3>
      {description && (
        <p className="empty-state__description">{description}</p>
      )}
      {action && (
        <div className="empty-state__action">
          {action}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
